import type { Variants } from 'motion/react';
import { duration, ease, stagger, travel } from '../theme/tokens';

/**
 * How far into the viewport something must be before its entrance plays.
 * Vertical only: a bare "-60px" also shrinks the left and right edges, so on a
 * phone a narrow element near the side (a one-digit Counter, 37px in) never
 * counted as in view and stayed at its starting state forever.
 */
export const IN_VIEW_MARGIN = '-60px 0px';

/**
 * Shared Motion variants, built from the design tokens so motion and
 * styling stay in sync. Prefer these over inline `initial`/`animate` objects.
 */

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: duration.base, ease: ease.out } },
};

export const fadeUp: Variants = {
  hidden: { opacity: 0, y: travel.md },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: duration.slow, ease: ease.out },
  },
};

export const fadeUpLarge: Variants = {
  hidden: { opacity: 0, y: travel.lg },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: duration.slower, ease: ease.out },
  },
};

export const fadeLeft: Variants = {
  hidden: { opacity: 0, x: -travel.md },
  visible: { opacity: 1, x: 0, transition: { duration: duration.slow, ease: ease.out } },
};

export const fadeRight: Variants = {
  hidden: { opacity: 0, x: travel.md },
  visible: { opacity: 1, x: 0, transition: { duration: duration.slow, ease: ease.out } },
};

export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.96 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: duration.base, ease: ease.out },
  },
};

/**
 * Parent container that reveals its children one after another.
 * Pair with `staggerItem` on each child.
 */
export const staggerContainer = (
  childDelay: number = stagger.base,
  initialDelay = 0,
): Variants => ({
  hidden: {},
  visible: {
    transition: { staggerChildren: childDelay, delayChildren: initialDelay },
  },
});

export const staggerItem: Variants = fadeUp;

/** Tighter variant for dense lists and table rows. */
export const staggerItemTight: Variants = {
  hidden: { opacity: 0, y: travel.sm },
  visible: { opacity: 1, y: 0, transition: { duration: duration.base, ease: ease.out } },
};

/** Word-by-word reveal used by AnimatedText. */
export const wordContainer = (childDelay: number, initialDelay: number): Variants => ({
  hidden: {},
  visible: { transition: { staggerChildren: childDelay, delayChildren: initialDelay } },
});

export const wordItem: Variants = {
  hidden: { opacity: 0, y: '0.45em' },
  visible: {
    opacity: 1,
    y: '0em',
    transition: { duration: duration.slower, ease: ease.out },
  },
};

/** Hover/press feedback for cards and tiles. */
export const hoverLift = {
  whileHover: { y: -4, transition: { duration: duration.fast, ease: ease.out } },
  whileTap: { y: -1, scale: 0.995 },
};

/** Hover/press feedback for buttons and small controls. */
export const hoverPress = {
  whileHover: { scale: 1.02, transition: { duration: duration.fast, ease: ease.out } },
  whileTap: { scale: 0.97 },
};
